"use client";

import { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { Factory, RotateCcw, Search, Timer, Zap } from "lucide-react";

export type CatalogItem = {
  id: string;
  name: string;
  amount: number;
  perMinute?: number;
};

export type CatalogRecipe = {
  id: string;
  tier: number;
  facilityId: string;
  facilityName: string;
  facilityPower: number;
  craftingTime: number;
  inputs: CatalogItem[];
  outputs: CatalogItem[];
  searchText: string;
};

type FacilityOption = {
  id: string;
  name: string;
};

type RecipeDatabaseProps = {
  entries: CatalogRecipe[];
  facilities: FacilityOption[];
};

const numberFormatter = new Intl.NumberFormat("en", {
  maximumFractionDigits: 2,
});

export default function RecipeDatabase({
  entries,
  facilities,
}: RecipeDatabaseProps) {
  const [query, setQuery] = useState("");
  const [facilityId, setFacilityId] = useState("all");
  const [tier, setTier] = useState("all");
  const [visibleLimit, setVisibleLimit] = useState(48);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase();

    return entries.filter((entry) => {
      const matchesQuery =
        normalizedQuery.length === 0 ||
        entry.searchText.includes(normalizedQuery);
      const matchesFacility =
        facilityId === "all" || entry.facilityId === facilityId;
      const matchesTier = tier === "all" || entry.tier.toString() === tier;

      return matchesQuery && matchesFacility && matchesTier;
    });
  }, [entries, facilityId, query, tier]);

  useEffect(() => {
    setVisibleLimit(48);
  }, [facilityId, query, tier]);

  const visibleEntries = filteredEntries.slice(0, visibleLimit);

  const resetFilters = () => {
    setQuery("");
    setFacilityId("all");
    setTier("all");
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-3 border-y border-white/10 py-4 lg:grid-cols-[minmax(0,1fr)_220px_150px_auto]">
        <label className="relative block min-w-0">
          <span className="sr-only">Search recipes</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search item, input, or facility"
            className="h-10 w-full rounded-md border border-white/10 bg-[#0b0f0e] pl-10 pr-3 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-cyan-300/50"
          />
        </label>

        <label>
          <span className="sr-only">Filter by facility</span>
          <select
            value={facilityId}
            onChange={(event) => setFacilityId(event.target.value)}
            className="h-10 w-full rounded-md border border-white/10 bg-[#0b0f0e] px-3 text-sm text-foreground outline-none focus:border-cyan-300/50"
          >
            <option value="all">All facilities</option>
            {facilities.map((facility) => (
              <option key={facility.id} value={facility.id}>
                {facility.name}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="sr-only">Filter by item tier</span>
          <select
            value={tier}
            onChange={(event) => setTier(event.target.value)}
            className="h-10 w-full rounded-md border border-white/10 bg-[#0b0f0e] px-3 text-sm text-foreground outline-none focus:border-cyan-300/50"
          >
            <option value="all">All tiers</option>
            {[1, 2, 3, 4, 5].map((value) => (
              <option key={value} value={value}>
                Tier {value}
              </option>
            ))}
          </select>
        </label>

        <button
          type="button"
          onClick={resetFilters}
          className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-white/10 px-3 text-sm text-muted-foreground transition hover:border-white/20 hover:text-foreground"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <p className="text-muted-foreground">
          Showing <span className="font-medium text-foreground">{visibleEntries.length}</span>{" "}
          loaded of {filteredEntries.length} matches
        </p>
        <p className="text-xs text-muted-foreground">
          Output rates assume one continuously active facility.
        </p>
      </div>

      {filteredEntries.length > 0 ? (
        <div className="grid gap-3">
          {visibleEntries.map((entry) => (
            <article
              key={entry.id}
              className="grid min-w-0 gap-4 rounded-lg border border-white/10 bg-card/65 p-4 lg:grid-cols-[minmax(220px,0.8fr)_minmax(280px,1.4fr)_220px] lg:items-center"
            >
              <div className="min-w-0 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded border border-amber-300/25 bg-amber-300/10 px-2 py-1 text-[11px] font-semibold text-amber-100">
                    Tier {entry.tier}
                  </span>
                  <span className="break-all font-mono text-[11px] text-muted-foreground">
                    {entry.id}
                  </span>
                </div>
                <div className="grid gap-2">
                  {entry.outputs.map((output) => (
                    <div key={output.id} className="flex min-w-0 items-center gap-3">
                      <Image
                        src={`/images/items/${output.id}.png`}
                        alt={output.name}
                        width={40}
                        height={40}
                        className="h-10 w-10 shrink-0 object-contain"
                      />
                      <div className="min-w-0">
                        <h2 className="break-words text-sm font-semibold text-foreground">
                          {output.amount} x {output.name}
                        </h2>
                        <p className="text-xs text-cyan-100">
                          {numberFormatter.format(output.perMinute ?? 0)} per minute
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="min-w-0 border-y border-white/10 py-3 lg:border-x lg:border-y-0 lg:px-4 lg:py-0">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                  Inputs
                </p>
                <div className="flex flex-wrap gap-2">
                  {entry.inputs.map((input) => (
                    <div
                      key={`${entry.id}-${input.id}`}
                      className="flex min-w-0 items-center gap-2 rounded-md border border-white/10 bg-background/35 px-2.5 py-2"
                    >
                      <Image
                        src={`/images/items/${input.id}.png`}
                        alt={input.name}
                        width={28}
                        height={28}
                        className="h-7 w-7 shrink-0 object-contain"
                      />
                      <span className="break-words text-xs text-muted-foreground">
                        <strong className="font-medium text-foreground">{input.amount} x</strong>{" "}
                        {input.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <dl className="grid grid-cols-3 gap-3 lg:grid-cols-1">
                <div className="min-w-0">
                  <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Factory className="h-3.5 w-3.5" /> Facility
                  </dt>
                  <dd className="mt-1 break-words text-xs font-medium text-foreground">
                    {entry.facilityName}
                  </dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Timer className="h-3.5 w-3.5" /> Craft time
                  </dt>
                  <dd className="mt-1 text-xs font-medium text-foreground">
                    {numberFormatter.format(entry.craftingTime)}s
                  </dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                    <Zap className="h-3.5 w-3.5" /> Power
                  </dt>
                  <dd className="mt-1 text-xs font-medium text-foreground">
                    {numberFormatter.format(entry.facilityPower)}
                  </dd>
                </div>
              </dl>
            </article>
          ))}
          {visibleEntries.length < filteredEntries.length ? (
            <div className="flex flex-col items-center gap-2 border-t border-white/10 py-6">
              <button
                type="button"
                onClick={() => setVisibleLimit((current) => current + 48)}
                className="inline-flex h-10 items-center justify-center rounded-md border border-cyan-300/25 bg-cyan-300/10 px-4 text-sm font-medium text-cyan-100 transition hover:bg-cyan-300/15"
              >
                Show 48 more recipes
              </button>
              <p className="text-xs text-muted-foreground">
                {filteredEntries.length - visibleEntries.length} matching recipes remain
              </p>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="border-y border-white/10 py-12 text-center">
          <p className="text-sm font-medium text-foreground">No recipes match these filters.</p>
          <p className="mt-2 text-sm text-muted-foreground">
            Try a broader item name or reset the facility and tier filters.
          </p>
        </div>
      )}
    </div>
  );
}
